import React, { useState, useEffect } from 'react';
import { 
  ShareIcon, 
  XMarkIcon,
  EnvelopeIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useAuthContext } from '../../features/auth/context/AuthContext';

// Get API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Add debug flag for sharing modal
const DEBUG_SHARING = false;

/**
 * Standardized Sharing Management Modal Component
 * 
 * This component provides a consistent sharing experience across the app
 * with enhanced features for cashflow detail sharing and partner management.
 * 
 * Features:
 * - Partner connection status with full details
 * - Global settings with Cashflow Detail toggle
 * - Privacy settings with granular controls
 * - Partner actions and invitation management
 * - Consistent UI/UX across Dashboard and Wallet
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.user - Current user object
 * @param {Function} props.onSyncAccounts - Function to sync accounts
 * @param {Function} props.onViewActivityLog - Function to view activity log
 * @param {Function} props.onManageSharingSettings - Function to manage sharing settings
 * @param {Function} props.onViewSharedAccounts - Function to view shared accounts
 * @param {Function} props.onAdvancedSettings - Function to open advanced settings
 * @returns {JSX.Element} Sharing Management Modal component
 */
const SharingManagementModal = ({ 
  isOpen, 
  onClose, 
  user,
  onSyncAccounts,
  onViewActivityLog,
  onManageSharingSettings,
  onViewSharedAccounts,
  onAdvancedSettings
}) => {
  const { 
    generatePartnerInvite, 
    refreshUserData, 
    getPartnerInvitations,
    cancelPartnerInvite,
    resendPartnerInvite,
    acceptPartnerInvite,
    rejectPartnerInvite,
    removePartner 
  } = useAuthContext();
  
  // Modal state
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showAccessLevelModal, setShowAccessLevelModal] = useState(false);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('custom');
  const [pendingInvitationEmail, setPendingInvitationEmail] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  
  // Enhanced sharing settings
  const [showTransactionDetails, setShowTransactionDetails] = useState(true);
  const [showCashflowDetail, setShowCashflowDetail] = useState(false);
  const [showAccountBalances, setShowAccountBalances] = useState(true);
  const [showNetWorth, setShowNetWorth] = useState(false);
  const [realTimeNotifications, setRealTimeNotifications] = useState(false);

  // Enhanced logging for debugging
  useEffect(() => {
    if (DEBUG_SHARING && isOpen) {
      console.log('🔧 SharingManagementModal opened with user:', {
        userId: user?._id,
        email: user?.email,
        partnerId: user?.partnerId,
        createdPartner: user?.createdPartner,
        sharingSettings: user?.sharingSettings
      });
    }
  }, [isOpen, user]);

  // Load invitations when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInvitations();
      loadSharingSettings();
    }
  }, [isOpen]);

  // Get partner information with enhanced details
  const partnerFirstName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
  const partnerLastName = user?.partner?.lastName || user?.createdPartner?.lastName || '';
  const partnerName = `${partnerFirstName} ${partnerLastName}`.trim();
  const partnerEmail = user?.partner?.email || user?.createdPartner?.email || '';
  const partnerConnection = user?.partnerConnection || user?.createdPartner?.connection || {};
  const connectionDate = partnerConnection?.createdAt || user?.partner?.createdAt || user?.createdPartner?.createdAt;
  const hasPartner = !!(user?.partner || user?.createdPartner || user?.partnerId);
  const partnerStatus = hasPartner ? 'connected' : 'disconnected';

  // Format connection date
  const formatConnectionDate = (date) => {
    if (!date) return 'Recently';
    try {
      const connectionTime = new Date(date);
      const now = new Date();
      const diffTime = now - connectionTime;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return connectionTime.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  const formattedConnectionDate = formatConnectionDate(connectionDate);

  // Load sharing settings from API
  const loadSharingSettings = async () => {
    try {
      // Load current sharing settings from API
      const response = await fetch(`${API_BASE}/api/user-sharing/settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.sharingSettings) {
          const settings = data.sharingSettings;
          setShowTransactionDetails(settings.showTransactionDetails ?? true);
          setShowCashflowDetail(settings.showCashflowDetail ?? false);
          setShowAccountBalances(settings.showAccountBalances ?? true);
          setShowNetWorth(settings.showNetWorth ?? false);
          setRealTimeNotifications(settings.realTimeNotifications ?? false);
          return;
        }
      }
      
      // Fallback to user prop data if API fails
      const sharingSettings = user?.sharingSettings || {};
      setShowTransactionDetails(sharingSettings.showTransactionDetails ?? true);
      setShowCashflowDetail(sharingSettings.showCashflowDetail ?? false);
      setShowAccountBalances(sharingSettings.showAccountBalances ?? true);
      setShowNetWorth(sharingSettings.showNetWorth ?? false);
      setRealTimeNotifications(sharingSettings.realTimeNotifications ?? false);
    } catch (error) {
      console.error('Failed to load sharing settings:', error);
      // Fallback to user prop data on error
      const sharingSettings = user?.sharingSettings || {};
      setShowTransactionDetails(sharingSettings.showTransactionDetails ?? true);
      setShowCashflowDetail(sharingSettings.showCashflowDetail ?? false);
      setShowAccountBalances(sharingSettings.showAccountBalances ?? true);
      setShowNetWorth(sharingSettings.showNetWorth ?? false);
      setRealTimeNotifications(sharingSettings.realTimeNotifications ?? false);
    }
  };

  // Load partner invitations
  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const response = await getPartnerInvitations();
      
      if (response && response.invitations && Array.isArray(response.invitations)) {
        const currentUserId = user?.id;
        const currentUserEmail = user?.email ? user.email.toLowerCase() : null;
        
        const userSentInvitations = response.invitations.filter(inv => {
          const invSenderId = inv.sender?._id || inv.sender;
          const invSenderEmail = inv.sender?.email || inv.senderEmail;
          return invSenderId === currentUserId || 
                 (currentUserEmail && invSenderEmail && invSenderEmail.toLowerCase() === currentUserEmail);
        });
        
        const userReceivedInvitations = response.invitations.filter(inv => {
          const invRecipientEmail = inv.recipientEmail || inv.recipient?.email;
          return currentUserEmail && invRecipientEmail && invRecipientEmail.toLowerCase() === currentUserEmail;
        });
        
        setSentInvitations(userSentInvitations);
        setReceivedInvitations(userReceivedInvitations);
        
        console.log('📧 Loaded invitations:', {
          sent: userSentInvitations.length,
          received: userReceivedInvitations.length,
          total: response.invitations.length
        });
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
      toast.error('Failed to load partner invitations');
    } finally {
      setInvitationsLoading(false);
    }
  };

  // Handle sending invitation
  const handleSendInvitation = async () => {
    if (!invitationEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSendingInvitation(true);
      
      // Generate and send invitation
      const response = await generatePartnerInvite(invitationEmail);
      
      if (response && response.success) {
        toast.success('Partner invitation sent successfully!');
        setInvitationEmail('');
        setShowInvitationModal(false);
        
        // Refresh invitations list
        await loadInvitations();
      } else {
        throw new Error(response?.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.message || 'Failed to send partner invitation');
    } finally {
      setSendingInvitation(false);
    }
  };

  // Handle access level confirmation
  const handleConfirmAccessLevel = async () => {
    if (!pendingInvitationEmail) {
      toast.error('Please enter an email address first');
      return;
    }

    try {
      setSendingInvitation(true);
      
      const response = await generatePartnerInvite(pendingInvitationEmail, {
        accessLevel: selectedAccessLevel,
        shareTransactionDetails: selectedAccessLevel === 'full',
        shareCashflowDetail: selectedAccessLevel === 'full',
        shareAccountBalances: selectedAccessLevel === 'full',
        shareNetWorth: selectedAccessLevel === 'full'
      });

      if (response && response.success) {
        toast.success('Partner invitation sent with access level!');
        setPendingInvitationEmail('');
        setShowAccessLevelModal(false);
        
        // Refresh invitations list
        await loadInvitations();
      } else {
        throw new Error(response?.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Failed to send invitation with access level:', error);
      toast.error(error.message || 'Failed to send partner invitation');
    } finally {
      setSendingInvitation(false);
    }
  };

  // Handle canceling invitation
  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelPartnerInvite(invitationId);
      toast.success('Invitation cancelled successfully');
      await loadInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  // Handle resending invitation
  const handleResendInvitation = async (invitationId) => {
    try {
      await resendPartnerInvite(invitationId);
      toast.success('Invitation resent successfully');
      await loadInvitations();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  // Handle accepting invitation
  const handleAcceptInvitation = async (invitationId) => {
    try {
      setActionInProgress(true);
      const response = await acceptPartnerInvite(invitationId, false);
      
      if (response && response.success) {
        toast.success('Partner invitation accepted! You are now connected.');
        await refreshUserData();
        await loadInvitations();
        onClose(); // Close modal after successful connection
      } else {
        throw new Error(response?.message || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle rejecting invitation
  const handleRejectInvitation = async (invitationId) => {
    try {
      setActionInProgress(true);
      await rejectPartnerInvite(invitationId);
      toast.success('Invitation declined');
      await loadInvitations();
    } catch (error) {
      console.error('Failed to reject invitation:', error);
      toast.error('Failed to decline invitation');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle disconnecting partner
  const handleDisconnectPartner = async () => {
    try {
      setDisconnecting(true);
      await removePartner();
      
      toast.success('Partner disconnected successfully');
      setShowDisconnectConfirm(false);
      
      // Refresh user data
      await refreshUserData();
      
      // Close modal after disconnect
      onClose();
    } catch (error) {
      console.error('Failed to disconnect partner:', error);
      toast.error('Failed to disconnect partner');
    } finally {
      setDisconnecting(false);
    }
  };

  // Enhanced sharing settings API call with better error handling
  const updateSharingSettingsAPI = async (setting, value) => {
    try {
      if (DEBUG_SHARING) {
        console.log('🔧 Updating sharing setting:', { setting, value });
      }

      const response = await fetch(`${API_BASE}/api/user-sharing/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          [setting]: value
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update ${setting}`);
      }

      const result = await response.json();
      
      if (DEBUG_SHARING) {
        console.log('✅ Sharing setting updated successfully:', result);
      }

      return result;
    } catch (error) {
      console.error('❌ Error updating sharing setting:', error);
      throw error;
    }
  };

  // Enhanced toggle function with better error handling
  const toggleSharingSetting = async (setting, currentValue, stateUpdater) => {
    try {
      setActionInProgress(true);
      
      if (DEBUG_SHARING) {
        console.log('🔧 Toggling sharing setting:', { setting, currentValue, newValue: !currentValue });
      }

      // Update sharing settings API call (separate from account sync)
      const result = await updateSharingSettingsAPI(setting, !currentValue);
      
      // Update state only after successful API call
      stateUpdater();
      
      const settingDisplayName = setting.replace(/([A-Z])/g, ' $1').toLowerCase();
      toast.success(`${settingDisplayName} sharing updated`);
      
      // Refresh user data to get updated settings
      if (refreshUserData) {
        await refreshUserData();
      }

      if (DEBUG_SHARING) {
        console.log('✅ Sharing setting toggle completed successfully');
      }
      
    } catch (error) {
      console.error(`❌ Failed to toggle ${setting}:`, error);
      toast.error(error.message || `Failed to update ${setting.replace(/([A-Z])/g, ' $1').toLowerCase()} setting`);
    } finally {
      setActionInProgress(false);
    }
  };

  // Enhanced sync accounts with better error handling
  const handleSyncAccounts = async () => {
    try {
      setActionInProgress(true);
      
      if (DEBUG_SHARING) {
        console.log('🔧 Starting account sync...');
      }

      // First, bulk enable sharing for all accounts if global settings are enabled
      let accountsUpdated = 0;
      try {
        const walletService = (await import('../../features/wallet/services/walletService')).default;
        const bulkResult = await walletService.bulkEnableAccountSharing();
        if (bulkResult.success) {
          accountsUpdated = bulkResult.updatedCount || 0;
          console.log(`✅ Enabled sharing for ${accountsUpdated} accounts`);
          if (accountsUpdated > 0) {
            toast.success(`Enabled sharing for ${accountsUpdated} accounts`);
          }
        }
      } catch (bulkError) {
        console.warn('⚠️ Bulk enable sharing failed (non-critical):', bulkError);
        // Don't fail the whole sync if this fails
      }

      if (onSyncAccounts) {
        await onSyncAccounts();
        toast.success('Accounts synced successfully');
        
        if (DEBUG_SHARING) {
          console.log('✅ Account sync completed successfully');
        }
      } else {
        throw new Error('Sync function not available');
      }
      
      // Force a page refresh after bulk enable to ensure partner accounts are visible
      // This is necessary because the partner account query happens on the other user's side
      if (accountsUpdated > 0) {
        console.log('🔄 Accounts were updated - partner should refresh their page to see changes');
        // Use toast.success instead of toast.info (toast.info may not be available)
        if (toast && typeof toast.success === 'function') {
          toast.success(`Enabled sharing for ${accountsUpdated} accounts! Partner should refresh to see changes.`, { duration: 5000 });
        }
      }
      
    } catch (error) {
      console.error('❌ Account sync failed:', error);
      
      // Check if it's a server error (500) and provide specific messaging
      if (error.response?.status === 500) {
        toast.error('Server error during sync. Your settings were saved, but account sync failed. Please try reconnecting your bank account.');
      } else if (error.response?.status === 400) {
        toast.error('Bank connection expired. Please reconnect your account.');
      } else {
        toast.error(error.message || 'Failed to sync accounts');
      }
      
      if (DEBUG_SHARING) {
        console.log('❌ Account sync error details:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
      }
    } finally {
      setActionInProgress(false);
    }
  };

  // Enhanced cashflow detail toggle with separation from account sync
  const handleCashflowDetailToggle = async () => {
    try {
      setActionInProgress(true);
      
      if (DEBUG_SHARING) {
        console.log('🔧 Toggling cashflow detail:', { currentValue: showCashflowDetail });
      }

      // Update the sharing setting first (this is the important part)
      const result = await updateSharingSettingsAPI('showCashflowDetail', !showCashflowDetail);
      
      // Update local state
      setShowCashflowDetail(!showCashflowDetail);
      
      // Show success message
      const message = !showCashflowDetail 
        ? 'Cashflow detail sharing enabled - Partner now has full access to all cashflow data'
        : 'Cashflow detail sharing disabled - Partner access to cashflow data revoked';
      
      toast.success(message);
      
      // Refresh user data
      if (refreshUserData) {
        await refreshUserData();
      }

      if (DEBUG_SHARING) {
        console.log('✅ Cashflow detail toggle completed successfully');
      }

      // OPTIONAL: Try to sync accounts but don't fail if it doesn't work
      try {
        if (DEBUG_SHARING) {
          console.log('🔧 Attempting optional account sync...');
        }
        
        if (onSyncAccounts) {
          await onSyncAccounts();
          
          if (DEBUG_SHARING) {
            console.log('✅ Optional account sync completed');
          }
        }
      } catch (syncError) {
        console.warn('⚠️ Account sync failed, but sharing settings were saved:', syncError);
        
        // Don't show error toast for optional sync failure
        if (DEBUG_SHARING) {
          console.log('⚠️ Optional sync failed, but that\'s okay - sharing settings are saved');
        }
      }
      
    } catch (error) {
      console.error('❌ Failed to toggle cashflow detail:', error);
      toast.error(error.message || 'Failed to update cashflow detail setting');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle other privacy settings
  const handlePrivacySettingToggle = async (setting, enabled) => {
    try {
      setActionInProgress(true);
      
      // Prepare the request body with the specific setting
      const requestBody = {};
      let stateUpdater;
      
      switch (setting) {
        case 'accountBalances':
          requestBody.showAccountBalances = enabled;
          stateUpdater = () => setShowAccountBalances(enabled);
          break;
        case 'netWorth':
          requestBody.showNetWorth = enabled;
          stateUpdater = () => setShowNetWorth(enabled);
          break;
        case 'realTimeNotifications':
          requestBody.realTimeNotifications = enabled;
          stateUpdater = () => setRealTimeNotifications(enabled);
          break;
        default:
          throw new Error('Unknown privacy setting');
      }
      
      // Call API to update privacy settings
      const response = await fetch(`${API_BASE}/api/user-sharing/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || `Failed to update ${setting.replace(/([A-Z])/g, ' $1').toLowerCase()} setting`);
      }

      // Update state only after successful API call
      stateUpdater();
      
      toast.success(`${setting.replace(/([A-Z])/g, ' $1').toLowerCase()} sharing updated`);
      
      // Refresh user data to get updated settings
      if (refreshUserData) {
        await refreshUserData();
      }
    } catch (error) {
      console.error(`Failed to toggle ${setting}:`, error);
      toast.error(error.message || `Failed to update ${setting.replace(/([A-Z])/g, ' $1').toLowerCase()} setting`);
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle quick actions
  const handleManageSharingSettings = () => {
    if (onManageSharingSettings && typeof onManageSharingSettings === 'function') {
      onManageSharingSettings();
    }
    // Keep modal open for this action
  };

  const handleViewSharedAccounts = () => {
    if (onViewSharedAccounts) {
      onViewSharedAccounts();
    }
    onClose(); // Close modal after navigating
  };

  const handleAdvancedSettings = () => {
    if (onAdvancedSettings) {
      onAdvancedSettings();
    }
    // Keep modal open for this action
  };

  const handleViewActivityLog = () => {
    if (onViewActivityLog) {
      onViewActivityLog();
    }
    // Keep modal open for this action
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Sharing Management Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto m-4">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="bg-orange-100 rounded-full p-3 mr-4">
                <ShareIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Sharing Management</h2>
                <p className="text-gray-600">
                  {hasPartner ? `Manage your financial sharing with ${partnerName}` : 'Set up financial sharing with a partner'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {hasPartner ? (
              <>
                {/* Enhanced Connected Partner Section */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="bg-green-100 rounded-full p-3 mr-4">
                        <UserIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-green-900">
                          Connected to {partnerName}
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          <strong>Email:</strong> {partnerEmail}
                        </p>
                        <p className="text-sm text-green-700">
                          <strong>Connected:</strong> {formattedConnectionDate}
                          {connectionDate && (
                            <span className="text-xs text-green-600 ml-2">
                              ({new Date(connectionDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })})
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-green-700 mt-2">
                          Financial sharing is active
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Connected
                      </span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Sharing Settings Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Global Settings */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Global Settings</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Cashflow Detail</label>
                          <p className="text-xs text-gray-500 mt-1">
                            When enabled, provides full access to all cashflow tabs/data (Income, Debt, Budget, Assets, Expenses, Summary, Savings)
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showCashflowDetail}
                            onChange={(e) => handleCashflowDetailToggle(e.target.checked)}
                            disabled={actionInProgress}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Transaction Details</label>
                          <p className="text-xs text-gray-500">Show transaction details for shared accounts</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showTransactionDetails}
                            onChange={(e) => handleTransactionDetailsToggle(e.target.checked)}
                            disabled={actionInProgress}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Settings */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Show Account Balances</label>
                          <p className="text-xs text-gray-500">Display account balance amounts</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showAccountBalances}
                            onChange={(e) => handlePrivacySettingToggle('accountBalances', e.target.checked)}
                            disabled={actionInProgress}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Show Net Worth</label>
                          <p className="text-xs text-gray-500">Display calculated net worth</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showNetWorth}
                            onChange={(e) => handlePrivacySettingToggle('netWorth', e.target.checked)}
                            disabled={actionInProgress}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Real-time Notifications</label>
                          <p className="text-xs text-gray-500">Send notifications for account changes</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={realTimeNotifications}
                            onChange={(e) => handlePrivacySettingToggle('realTimeNotifications', e.target.checked)}
                            disabled={actionInProgress}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Partner Actions */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Partner Actions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <button 
                      onClick={handleSyncAccounts}
                      disabled={actionInProgress}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {actionInProgress ? 'Syncing...' : 'Sync Accounts'}
                    </button>
                    <button 
                      onClick={handleViewActivityLog}
                      disabled={true}
                      className="px-4 py-2 text-sm bg-gray-400 text-gray-300 rounded-lg cursor-not-allowed transition-colors"
                      title="Coming soon"
                    >
                      View Activity Log
                    </button>
                    <button 
                      onClick={handleViewSharedAccounts}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Shared Accounts
                    </button>
                    <button 
                      onClick={() => setShowDisconnectConfirm(true)}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Disconnect Partner
                    </button>
                  </div>
                </div>

                {/* Quick Actions Section */}
                <div className="p-6 bg-gray-50 border-t rounded-b-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    <button 
                      onClick={handleManageSharingSettings}
                      className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ShareIcon className="h-4 w-4 mr-2 text-gray-600" />
                      <span className="text-sm text-gray-700">Manage Sharing Settings</span>
                    </button>
                    <button 
                      onClick={handleViewSharedAccounts}
                      className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <EyeIcon className="h-4 w-4 mr-2 text-gray-600" />
                      <span className="text-sm text-gray-700">View Shared Accounts</span>
                    </button>
                    <button 
                      onClick={handleAdvancedSettings}
                      disabled={true}
                      className="flex items-center px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg cursor-not-allowed transition-colors"
                      title="Coming soon"
                    >
                      <Cog6ToothIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-sm text-gray-400">Advanced Settings</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* No Partner Connected - Invitation Section */}
                <div className="text-center mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <UserPlusIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Connect with Your Partner
                    </h3>
                    <p className="text-sm text-blue-700 mb-4">
                      Share your financial information securely with your partner
                    </p>
                    <button
                      onClick={() => setShowInvitationModal(true)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Send Invitation
                    </button>
                  </div>
                </div>

                {/* Sent Invitations */}
                {sentInvitations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Sent Invitations</h4>
                    <div className="space-y-2">
                      {sentInvitations.map((invitation) => (
                        <div key={invitation._id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center">
                            <ClockIcon className="h-5 w-5 text-yellow-600 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900">
                                {invitation.recipientEmail}
                              </p>
                              <p className="text-xs text-yellow-700">
                                Sent {new Date(invitation.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleResendInvitation(invitation._id)}
                              className="text-xs bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700"
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => handleCancelInvitation(invitation._id)}
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Received Invitations */}
                {receivedInvitations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Received Invitations</h4>
                    <div className="space-y-2">
                      {receivedInvitations.map((invitation) => {
                        const senderName = invitation.sender && typeof invitation.sender === 'object'
                          ? `${invitation.sender.firstName || ''} ${invitation.sender.lastName || ''}`.trim() || 'Partner'
                          : 'Partner';
                        
                        const senderEmail = invitation.sender && typeof invitation.sender === 'object'
                          ? invitation.sender.email
                          : invitation.senderEmail || '';

                        return (
                          <div key={invitation._id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center">
                              <EnvelopeIcon className="h-5 w-5 text-green-600 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-green-900">
                                  From: {senderName}
                                </p>
                                {senderEmail && (
                                  <p className="text-xs text-green-700">
                                    {senderEmail}
                                  </p>
                                )}
                                <p className="text-xs text-green-700">
                                  Received {new Date(invitation.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleRejectInvitation(invitation._id)}
                                className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                              >
                                Decline
                              </button>
                              <button
                                onClick={() => handleAcceptInvitation(invitation._id)}
                                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                              >
                                Accept
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Send Partner Invitation</h3>
                <button
                  onClick={() => setShowInvitationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Partner's Email Address
                  </label>
                  <input
                    type="email"
                    value={invitationEmail}
                    onChange={(e) => setInvitationEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="partner@example.com"
                  />
                </div>

                {/* Sharing Settings Preview */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Your Current Sharing Settings</h4>
                  <p className="text-xs text-gray-600 mb-3">These settings control what your partner can see. You can change them anytime after connecting.</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Transaction Details</p>
                        <p className="text-xs text-gray-500">Show individual transaction details</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full transition-colors ${showTransactionDetails ? 'bg-green-500' : 'bg-gray-300'} flex items-center px-1`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${showTransactionDetails ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Cashflow Detail</p>
                        <p className="text-xs text-gray-500">Full access to Income, Debt, Budget, Assets, Expenses, Savings</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full transition-colors ${showCashflowDetail ? 'bg-green-500' : 'bg-gray-300'} flex items-center px-1`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${showCashflowDetail ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Account Balances</p>
                        <p className="text-xs text-gray-500">Display account balance amounts</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full transition-colors ${showAccountBalances ? 'bg-green-500' : 'bg-gray-300'} flex items-center px-1`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${showAccountBalances ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Net Worth</p>
                        <p className="text-xs text-gray-500">Display calculated net worth</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full transition-colors ${showNetWorth ? 'bg-green-500' : 'bg-gray-300'} flex items-center px-1`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${showNetWorth ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-blue-600 mt-3">
                    💡 Tip: Go to "Global Settings" to change these before sending the invitation.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowInvitationModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInvitation}
                    disabled={!invitationEmail || sendingInvitation}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendingInvitation ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Disconnect Partner</h3>
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <p className="text-sm text-gray-700 text-center">
                  Are you sure you want to disconnect from {partnerName}? This will stop all financial sharing.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnectPartner}
                  disabled={disconnecting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SharingManagementModal; 